/**
 * Audit Log Panel Component
 */

import { useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useAuditLogs } from '../hooks/useFeatureFlags';

const PAGE_SIZE = 20;

export function AuditLogPanel() {
  const { t, language } = useI18n();
  const [page, setPage] = useState(0);
  const { data, isLoading, error } = useAuditLogs(PAGE_SIZE, page * PAGE_SIZE);

  const logs = data?.logs ?? [];
  const hasMore = logs.length === PAGE_SIZE;

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const getActionBadge = (action: string) => {
    const styles: Record<string, string> = {
      CREATE: 'bg-green-100 text-green-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700',
    };
    return styles[action] || 'bg-gray-100 text-gray-700';
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      CREATE: t('featureFlags.auditLog.actions.create'),
      UPDATE: t('featureFlags.auditLog.actions.update'),
      DELETE: t('featureFlags.auditLog.actions.delete'),
    };
    return labels[action] || action;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-semibold">{t('featureFlags.auditLog.errorLoading')}</h3>
            <p className="text-red-600 text-sm">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (logs.length === 0 && page === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>{t('featureFlags.auditLog.noLogs')}</p>
        <p className="text-sm mt-1">{t('featureFlags.auditLog.noLogsHint')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">{t('featureFlags.auditLog.title')}</h3>
        <p className="text-sm text-gray-500">
          {t('featureFlags.auditLog.description')}
        </p>
      </div>

      {/* Logs List */}
      <div className="space-y-3">
        {logs.map((log) => (
          <div
            key={log.id}
            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded ${getActionBadge(log.action)}`}
                >
                  {getActionLabel(log.action)}
                </span>
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-900">
                    <span className="font-medium">
                      v{log.old_version || 0} → v{log.new_version || 0}
                    </span>
                  </div>
                  {log.change_reason && (
                    <p className="text-sm text-gray-600 mt-1">
                      {log.change_reason}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-gray-500">
                <div className="flex items-center gap-1 justify-end">
                  <User className="w-3 h-3" />
                  <span className="font-mono">{log.changed_by}</span>
                </div>
                <div className="flex items-center gap-1 justify-end mt-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(log.created_at)}</span>
                </div>
                {log.ip_address && (
                  <div className="mt-1 text-gray-400">{log.ip_address}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className={`
            flex items-center gap-1 px-3 py-1.5 text-sm rounded
            ${
              page === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.previous')}
        </button>
        <span className="text-sm text-gray-500">{t('featureFlags.auditLog.page', { page: String(page + 1) })}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={!hasMore}
          className={`
            flex items-center gap-1 px-3 py-1.5 text-sm rounded
            ${
              !hasMore
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          {t('common.next')}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
